import React, { useState, useEffect } from 'react';
import traderbroLogo from '../assets/traderbro-logo.svg';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('demo');
  const [scrolled, setScrolled] = useState(false);

  // Detect which section is in view
  useEffect(() => {
    const sections = ['demo', 'how-it-works', 'features', 'about'];

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for navbar height + buffer
      setScrolled(scrollPosition > 50);

      // Find the section currently in view
      let currentSection = 'demo';

      for (let i = 0; i < sections.length; i++) {
        const section = document.getElementById(sections[i]);
        if (section) {
          const sectionTop = section.offsetTop;
          const sectionHeight = section.offsetHeight;

          // Check if scroll position is within this section
          if (scrollPosition >= sectionTop - 100 && scrollPosition < sectionTop + sectionHeight - 100) {
            currentSection = sections[i];
            break;
          }
        }
      }

      // If scrolled past all sections, set the last one as active
      const lastSection = document.getElementById(sections[sections.length - 1]);
      if (lastSection && scrollPosition >= lastSection.offsetTop - 100) {
        currentSection = sections[sections.length - 1];
      }

      setActiveSection(currentSection);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledScroll, { passive: true });
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  const smoothScroll = (e, targetId) => {
    e.preventDefault();
    const target = document.querySelector(targetId);
    if (target) {
      const offsetTop = target.offsetTop - 64; // Account for fixed nav height
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      setIsMenuOpen(false); // Close mobile menu after clicking
    }
  };

  const navLinks = [
    { id: 'demo', href: '#demo', label: 'Demo' },
    { id: 'how-it-works', href: '#how-it-works', label: 'How It Works' },
    { id: 'features', href: '#features', label: 'Features' },
    { id: 'research', href: '/research', label: 'Research', isStatic: true },
    { id: 'about', href: '#about', label: 'About' },
  ];

  return (
    <nav className={`w-full sticky top-0 z-50 border-b shadow-lg transition-all duration-300 ${scrolled
        ? 'bg-brand-dark/98 backdrop-blur-md border-gray-700'
        : 'bg-brand-dark/95 backdrop-blur-md border-gray-700'
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center hover:opacity-80 transition-opacity duration-200">
              <img src={traderbroLogo} alt="TraderBro" className="h-12 w-auto" />
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navLinks.map((link) => {
                const isActive = activeSection === link.id;
                return (
                  <a
                    key={link.id}
                    href={link.href}
                    onClick={(e) => link.isStatic ? null : smoothScroll(e, link.href)}
                    className="relative text-brand-accent hover:text-brand-light transition-all duration-200 hover:scale-105 inline-block group"
                  >
                    <span className={`relative ${isActive ? 'text-brand-cta' : ''}`}>
                      {link.label}
                    </span>
                    {/* Green underline indicator */}
                    <span
                      className={`absolute bottom-0 left-0 right-0 h-0.5 bg-[#00D492] transition-all duration-300 ${isActive ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0 group-hover:opacity-50 group-hover:scale-x-100'
                        }`}
                      style={{ transformOrigin: 'center' }}
                    ></span>
                  </a>
                );
              })}
            </div>
          </div>


          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-brand-accent hover:text-brand-light focus:outline-none focus:text-brand-light"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden animate-fade-in">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-brand-dark border-t border-gray-700">
              {navLinks.map((link) => {
                const isActive = activeSection === link.id;
                return (
                  <a
                    key={link.id}
                    href={link.href}
                    onClick={(e) => link.isStatic ? null : smoothScroll(e, link.href)}
                    className={`relative block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 ${isActive
                        ? 'text-brand-cta bg-brand-cta/10'
                        : 'text-brand-accent hover:text-brand-light'
                      }`}
                  >
                    <span>{link.label}</span>
                    {/* Green left border indicator for mobile */}
                    {isActive && (
                      <span className="absolute left-0 top-0 bottom-0 w-1 bg-[#00D492] rounded-l-md"></span>
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
